import {
  Component,
  OnInit,
  Input,
  Inject,
  OnDestroy,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Page } from '@bloomreach/spa-sdk';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { AppStateService } from '@services/app-state.service';
import { WidenService } from '@services/widen.service';
import { FiltersService } from '@search/services/filters.service';
import { FtSearchService } from '@search/services/ftsearch.service';
import { ConfigService } from '@search/services/config.service';
import { SearchServiceToken } from '@frk/eds-components';
import {
  InsightsPromoSpot,
  PromoCollection,
  TargetedLink,
} from '@marketing/insights/insights.interface';
import { Link, LinkCollection } from '@types';
import { FtSearchOptions } from '@search/interfaces/search.interface';
import { HttpParams } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LinkService } from '@services/link.service';
import { Logger } from '@utils/logger';
import { SegmentService } from '@services/segment.service';
import { ArticleService } from '@marketing/article/services/article.service';

const logger = Logger.getLogger('InsightsSidebarComponent');

@Component({
  selector: 'ft-insights-sidebar',
  templateUrl: './insights-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsightsSidebarComponent
  extends AbstractBaseComponent
  implements OnInit, OnChanges, OnDestroy {
  /**
   * Bloomreach document data
   */
  @Input() documentData: any;

  public expand: boolean[] = [];
  /**
   * Show hide top 4 promo card
   */
  @Input() enableTopFour: boolean;

  /**
   * Show hide collections
   */
  @Input() enableCollections = true;

  /**
   * Bloomreach page object
   */
  @Input() page: Page;

  /**
   * Translation object
   */
  @Input() translationLabel: any;

  /**
   * Object for promo card and collections
   */
  collectionData: PromoCollection;

  /**
   * Observable for component destroy
   */
  componentDestroyed$: Subject<boolean> = new Subject();

  /**
   *  Promo content
   */
  promoContent: InsightsPromoSpot;

  /**
   * Targeted Link
   */
  targetedLink: TargetedLink[] = [];
  shouldShowTopContent = false;

  /**
   * Article search api options
   */
  options: FtSearchOptions = {
    page: 0,
    term: '*',
    dataSource: 'dev',
    articleType: 'insight',
    current: '',
    config: {
      ftSearchUrl: '',
      ftInsightsUrl: '',
      httpParams: new HttpParams(),
      httpOptions: {},
    },
    // TODO verify if we use it and move to config
    counters: {
      funds: 4,
      literature: 4,
      pages: 4,
    },
  };

  /**
   * Collection links
   */
  collections = [];

  /**
   * Collection links
   */
  collection = [];

  /**
   * Segment Tag
   */
  segmentTags: string;

  /**
   * Constructor
   */
  constructor(
    private widenService: WidenService,
    private appStateService: AppStateService,
    @Inject(SearchServiceToken) private searchService: FtSearchService,
    private filters: FiltersService,
    private configService: ConfigService,
    private cd: ChangeDetectorRef,
    private linkService: LinkService,
    private segmentService: SegmentService,
    private articleService: ArticleService
  ) {
    super();
  }

  /**
   * Ng On Changes
   */
  ngOnChanges(): void {
    this.collectionData = this.getCollectionData();
  }

  /**
   * Get promo card and collection data
   * from bloomreach document object
   */
  getCollectionData() {
    const content: PromoCollection = this.documentData;
    content.contentTop = {};
    content.contentBottom = {};

    this.promoContent = content?.insightsLibraryPromoSpot;
    // WDE-6375 : Extract targeted link from pageContent and add as per site and actor
    this.targetedLink = content?.targetedLink;
    this.targetedLink.forEach((targetval) => {
      this.segmentTags = this.segmentService.getCurrentSegment()?.insightAudiences;
      targetval.isLinkVisible = this.articleService.isSegmentTagAvailable(
        this.segmentTags,
        targetval.targeting
      );
      if (targetval?.link?.linkCollection?.length) {
        const linkBlock: LinkCollection = targetval?.link?.linkCollection[0];
        linkBlock.href = this.linkService.getCTALink(
          this.page,
          linkBlock,
          this.appStateService?.getHomePageUrl()
        );
        linkBlock.externalLink = this.getExternalLink(
          targetval.link?.external,
          targetval.link?.externalSuffix
        );
      }
    });
    if (content.heading !== '') {
      this.shouldShowTopContent = true;

      this.setContentTop(content);
    }

    if (content?.featuredHeading) {
      content.contentBottom.title = content.featuredHeading;
    }

    if (content?.featuredLinks?.length) {
      content.contentBottom.articles = [];
      content.featuredLinks.forEach((element: Link, index) => {
        const featuredLink = element.linkCollection[0];
        if (featuredLink?.displayText) {
          content.contentBottom.articles.push({
            indexKey: index + 1,
            description: featuredLink.displayText,
            href: this.linkService.getCTALink(
              this.page,
              featuredLink,
              this.appStateService?.getHomePageUrl()
            ),
          });
        }
      });
    } else {
      if (this.enableTopFour) {
        this.getDynamicFeaturedLinks(
          content.articleFilters,
          content.maxResults
        );
      }
    }

    if (content?.flyerCopy && content?.flyer) {
      content.contentBottom.summary = content.flyerCopy;
      content.contentBottom.linkText = this.translationLabel?.downloadFlayer
        ? this.translationLabel.downloadFlayer
        : 'Download Flyer';
      if (content.flyer?.length) {
        content.contentBottom.linkUrl = this.widenService.getWidenAssetUrl(
          content.flyer[0].widenDocument
        );
      } else {
        content.contentBottom.linkUrl = this.widenService.getWidenAssetUrl(
          content.flyer.widenDocument
        );
      }
    }

    /** Below code used for old sidebard document type */
    if (content?.collections?.length) {
      content.collections.forEach((element: Link, index) => {
        if (element.linkCollection?.length) {
          element = element.linkCollection[0];
          if (element) {
            element.signInRequired = element.signInLink;
            element.url = this.linkService.getCTALink(
              this.page,
              element,
              this.appStateService?.getHomePageUrl()
            );
            this.collection.push(element);
          }
        }
      });
    }

    /** Below code used for new sidebard document type */
    if (content?.collection?.length) {
      content.collection.forEach((element) => {
        element?.theme?.forEach((themecollection, index) => {
          if (themecollection?.linkCollection?.length) {
            element.theme[index] = themecollection?.linkCollection[0];
            const isTargetAvailable = this.segmentService.isSegmentTagAvailable(
              element.theme[index].targeting
            );
            element.theme[index].isTargeteAvailable = isTargetAvailable;
            element.hasValidLink = element.hasValidLink || isTargetAvailable;
            element.theme[index].signInRequired = themecollection?.signInLink;
            element.theme[index].url = this.linkService.getCTALink(
              this.page,
              element.theme[index],
              this.appStateService?.getHomePageUrl()
            );
          }
        });
        this.collections.push(element);
      });
    }
    return content;
  }

  /**
   * Set external link class
   * @param external - is link external true|false
   * @param externalSuffix - suffix to add to external link
   */
  private getExternalLink(external: boolean, externalSuffix: string): string {
    if (external) {
      const externalLink = 'external-link';
      if (externalSuffix) {
        // Adding external link suffix
        return externalLink + '--' + externalSuffix;
      }
      return externalLink;
    }
    return '';
  }

  private setContentTop(content: PromoCollection) {
    if (content?.heading) {
      content.contentTop.title = content.heading;
    }
    if (content?.bodyCopy) {
      content.contentTop.summary = content.bodyCopy;
    }

    content.contentTop.linkText = '';

    if (Array.isArray(content?.link)) {
      // WDE-3501: support muliple links
      const targetLink = content?.link.find((linkElement: Link) => {
        return this.segmentService.isSegmentTagAvailable(
          linkElement?.linkCollection[0]?.targeting
        );
      });

      if (targetLink) {
        content.contentTop.linkUrl = this.linkService.getCTALink(
          this.page,
          targetLink.linkCollection[0],
          this.appStateService?.getHomePageUrl()
        );

        content.contentTop.linkText = targetLink.linkCollection[0]?.displayText;

        content.contentTop.iconType = 'carrot-right';
      }
    } else if (content?.link?.linkCollection?.length) {
      // support old functionality
      const headingLink: Link = content.link?.linkCollection[0];
      content.contentTop.linkUrl = this.linkService.getCTALink(
        this.page,
        headingLink,
        this.appStateService?.getHomePageUrl()
      );

      headingLink.isTargeteAvailable = this.segmentService.isSegmentTagAvailable(
        headingLink.targeting
      );

      content.contentTop.linkText = headingLink?.isTargeteAvailable
        ? headingLink?.displayText
        : '';

      content.contentTop.iconType = 'carrot-right';
    }
  }

  /**
   * Get articles from api if
   * curated data is not available
   * @param selectedFilters object of selected filters
   */
  getDynamicFeaturedLinks(selectedFilters, maxResults: number) {
    const dynamicLinks = [];
    this.searchService.insightsTopFourOutput$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(({ results }) => {
        const dynamicResults = results.response?.hits?.hits;

        if (dynamicResults.length) {
          dynamicResults.forEach((element, index) => {
            let articleUrl;
            if (element._source?.relativePath?.startsWith('/')) {
              articleUrl =
                this.appStateService?.getSpaBaseUrl() +
                element._source?.relativePath;
            } else {
              articleUrl =
                this.appStateService?.getSpaBaseUrl() +
                '/' +
                element._source?.relativePath;
            }
            // only push if they have a title
            if (element._source?.title) {
              dynamicLinks.push({
                indexKey: index + 1,
                description: element._source.title,
                href: articleUrl,
              });
            }
          });
        }
        if (this.collectionData) {
          this.collectionData.contentBottom.articles = dynamicLinks;
        }
        this.cd.detectChanges();
      });
    this.configService
      .getConfig('insights')
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((config) => {
        this.options.config = config;
        if (maxResults) {
          this.options.counters.pages = maxResults;
        }
        const searchParams = {
          options: this.options,
          filters: this.setPreFilter(selectedFilters),
        };
        this.searchService.getInsightsTopFour(searchParams);
      });
    return dynamicLinks;
  }

  /**
   * Set pre filters for search
   */
  setPreFilter(selectedFilters) {
    const filters = selectedFilters;
    const preFilters = [];
    if (filters) {
      const keys = [
        'assetclass',
        'investmentteam',
        'investmenttheme',
        'publicationseries',
        'speciality',
      ];
      Object.entries(filters).forEach(([key, value]) => {
        if (keys.includes(key.toLowerCase())) {
          if (value) {
            const arr = [];
            arr.push(value);
            if (key === 'assetClass' || key === 'assetclass') {
              preFilters.push({
                fieldName: 'assetclassPage.exact',
                fieldValue: arr,
              });
            } else {
              preFilters.push({
                fieldName: key.toLowerCase() + '.exact',
                fieldValue: arr,
              });
            }
          }
        }
      });
    }
    return JSON.stringify(preFilters);
  }

  toggleExpanded(index): void {
    this.expand[index] = !this.expand[index];
  }

  /**
   * Ng On Destroy
   */
  ngOnDestroy(): void {
    // unsubscribe from Observables
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
